// src/components/CompanySelector.tsx
import React, { useState, useEffect, createContext, useContext } from 'react';
// import axios from 'axios';
import { Listbox } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
// import { useAuthStore } from '../context/AuthContext';
import { type Company, useCompanies } from '../hooks/use-companies';

interface CompanyContextType {
  selectedCompany: Company | null;
  setSelectedCompany: (company: Company | null) => void;
}

const CompanyContext = createContext<CompanyContextType | undefined>(undefined);

export const useCompany = () => {
  const context = useContext(CompanyContext);
  if (context === undefined) {
    throw new Error('useCompany must be used within a CompanyProvider');
  }
  return context;
};

export const CompanyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const { companies } = useCompanies();

  useEffect(() => {
    if (companies.length > 0) {
      setSelectedCompany((prev) => prev);
    }
  }, [companies]);

  return (
    <CompanyContext.Provider value={{ selectedCompany, setSelectedCompany }}>
      {children}
    </CompanyContext.Provider>
  );
};


const CompanySelector: React.FC = () => {
  const { selectedCompany, setSelectedCompany } = useCompany();
  const { companies } = useCompanies();

  // useEffect(() => {
  //   const fetchCompanies = async () => {
  //     try {
  //       const response = await axios.get<Company[]>(`${BACKEND_URL}/companies`);
  //       setCompanies(response.data);
  //       if (!selectedCompany && response.data.length > 0) {
  //         setSelectedCompany(response.data[0]);
  //       }
  //     } catch (error) {
  //       console.error('Failed to fetch companies:', error);
  //     }
  //   };
  //   if (isAuthenticated) {
  //     fetchCompanies();
  //   }
  // }, [selectedCompany, setSelectedCompany, BACKEND_URL]);

  useEffect(() => {
    if (!selectedCompany && companies.length > 0) {
      setSelectedCompany(companies[0]);
    }
  }, [companies, selectedCompany, setSelectedCompany]);


  return (
    <div className="w-52">
      <Listbox value={selectedCompany} onChange={setSelectedCompany}>
        <div className="relative mt-1">
          <Listbox.Button className="relative w-full cursor-default rounded-md bg-white py-2 pl-3 pr-10 text-left shadow-md focus:outline-none focus-visible:border-indigo-500 focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-orange-300 sm:text-sm">
            <span className="block truncate">{selectedCompany ? selectedCompany.name : 'Select Company'}</span>
            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
              <ChevronUpDownIcon
                className="h-5 w-5 text-gray-400"
                aria-hidden="true"
              />
            </span>
          </Listbox.Button>
          <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
            {companies.map((company) => (
              <Listbox.Option
                key={company.id}
                className={({ active }) =>
                  `relative cursor-default select-none py-2 pl-10 pr-4 ${active ? 'bg-blue-100 text-blue-900' : 'text-gray-900'
                  }`
                }
                value={company}
              >
                {({ selected }) => (
                  <>
                    <span
                      className={`block truncate ${selected ? 'font-medium' : 'font-normal'
                        }`}
                    >
                      {company.name}
                    </span>
                    {selected ? (
                      <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
                        <CheckIcon className="h-5 w-5" aria-hidden="true" />
                      </span>
                    ) : null}
                  </>
                )}
              </Listbox.Option>
            ))}
          </Listbox.Options>
        </div>
      </Listbox>
    </div>
  );
};

export default CompanySelector;
